// Service API centralisé pour ELECTRA

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('electra_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('electra_token', token);
    } else {
      localStorage.removeItem('electra_token');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('electra_token');
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
      ...options.headers,
    };

    // Ajouter le token si disponible
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Gérer les erreurs HTTP
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Erreur ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  // Auth
  async login(email, password) {
    const data = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.access_token);
    return data;
  }

  async register(userData) {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async getMe() {
    return this.request('/api/auth/me');
  }

  logout() {
    // Signale la deconnexion au serveur avant d'oublier le jeton, pour que le
    // registre des connexions note l'heure de fin de session. Sans attendre
    // la reponse : la deconnexion locale ne doit jamais dependre du reseau.
    // Un jeton deja expire est ignore cote serveur (la session est close par
    // expiration, pas par une deconnexion).
    const token = this.getToken();
    if (token) {
      fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true,
      }).catch(() => { /* rien a faire : la deconnexion locale suit */ });
    }
    this.setToken(null);
    localStorage.removeItem('electra_user');
  }

  // Demo Data Endpoints
  async getProductionLines(siteId) {
    const params = new URLSearchParams();
    if (siteId) params.set('site_id', siteId);
    return this.request(`/api/demo/production-lines?${params.toString()}`);
  }

  async getZonesSummary(siteId = null) {
    const q = siteId ? `?site_id=${encodeURIComponent(siteId)}` : '';
    return this.request(`/api/demo/zones-summary${q}`);
  }

  // `periode` accepte : month, last_month, week, last_week, year, 30d, custom.
  // En mode custom, `debut` et `fin` sont des dates AAAA-MM-JJ, fin incluse.
  async getDirectionKpis(currency = 'CFA', siteId = null, periode = 'month', debut = null, fin = null) {
    const params = new URLSearchParams({ currency });
    if (siteId) params.set('site_id', siteId);
    if (periode) params.set('period', periode);
    if (periode === 'custom' && debut && fin) {
      params.set('start', debut);
      params.set('end', fin);
    }
    return this.request(`/api/demo/kpis/direction?${params.toString()}`);
  }

  async getComparison(period = 'yearly', siteId = null) {
    const params = new URLSearchParams({ period });
    if (siteId) params.set('site_id', siteId);
    return this.request(`/api/demo/comparison?${params.toString()}`);
  }

  async getMaintenance(siteId = null) {
    const q = siteId ? `?site_id=${encodeURIComponent(siteId)}` : '';
    return this.request(`/api/demo/maintenance${q}`);
  }

  async getRecentAlerts(limit = 10, siteId = null) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (siteId) params.set('site_id', siteId);
    return this.request(`/api/demo/alerts/recent?${params.toString()}`);
  }

  async getSites() {
    return this.request('/api/demo/sites');
  }

  async getMySites() {
    return this.request('/api/demo/my-sites');
  }

  async getProductionMetrics(siteId) {
    const qs = siteId ? `?site_id=${encodeURIComponent(siteId)}` : '';
    return this.request(`/api/demo/production-metrics${qs}`);
  }

  // Licenses
  async getAvailableFeatures() {
    return this.request('/api/licenses/available-features');
  }

  async getMyLicense() {
    return this.request('/api/licenses/my-license');
  }

  async validateLicense(licenseKey) {
    return this.request(`/api/licenses/validate/${licenseKey}`);
  }

  // Real-time Data Endpoints
  async getRealtimeLatest(siteId) {
    return this.request(`/api/realtime/latest/${siteId}`);
  }

  async getRealtimeMeter(siteId, meterId) {
    return this.request(`/api/realtime/latest/${siteId}/${meterId}`);
  }

  async getRealtimeHistory(siteId, hours = 24) {
    return this.request(`/api/realtime/history/${siteId}?hours=${hours}`);
  }

  async getSitesStatus() {
    return this.request('/api/realtime/sites/status');
  }

  async getMqttStats() {
    return this.request('/api/realtime/stats');
  }

  // ── Libre-service mot de passe (ajout) ─────────────────────────────
  // Permet a un utilisateur de definir lui-meme son mot de passe. La
  // reinitialisation par un administrateur reste disponible par ailleurs.

  async forgotPassword(email) {
    return this.request('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token, newPassword) {
    return this.request('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  }

  async changeOwnPassword(currentPassword, newPassword) {
    return this.request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  async getActiveAlerts(siteId = null) {
    const query = siteId ? `?site_id=${siteId}` : '';
    return this.request(`/api/realtime/alerts/active${query}`);
  }

  async getCompensationSummary(siteId) {
    const params = new URLSearchParams();
    if (siteId) params.set('site_id', siteId);
    return this.request(`/api/realtime/compensation/summary?${params.toString()}`);
  }

  async getCompensationComparison(siteId, periode = 'today', debut = null, fin = null) {
    const params = new URLSearchParams({ site_id: siteId || 'site_1', period: periode });
    if (periode === 'custom' && debut && fin) {
      params.set('start', debut);
      params.set('end', fin);
    }
    return this.request(`/api/realtime/compensation/comparison?${params.toString()}`);
  }

  async getConsumptionHistory(siteId, interval = '24h', meterId = null, startDate = null, endDate = null, lineId = null) {
    const params = new URLSearchParams({ interval });
    if (siteId) params.set('site_id', siteId);
    if (meterId) params.set('meter_id', meterId);
    // Une ligne precise : le serveur lit son circuit, pas le total du compteur.
    if (lineId) params.set('line_id', lineId);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    return this.request(`/api/realtime/consumption/history?${params.toString()}`);
  }

  async acknowledgeAlert(alertId) {
    return this.request(`/api/realtime/alerts/${alertId}/acknowledge`, {
      method: 'POST'
    });
  }

  // Performance Goals
  async getPerformanceGoals() {
    return this.request('/api/settings/performance-goals');
  }

  async savePerformanceGoals(goals) {
    return this.request('/api/settings/performance-goals', {
      method: 'POST',
      body: JSON.stringify(goals)
    });
  }

  // Shift Settings
  async getShiftSettings(siteId = 'site_1') {
    return this.request(`/api/settings/shifts?site_id=${siteId}`);
  }

  async saveShiftSettings(config) {
    return this.request('/api/settings/shifts', {
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  // Monitored Lines
  async getMonitoredLines(siteId) {
    const params = new URLSearchParams();
    if (siteId) params.set('site_id', siteId);
    return this.request(`/api/settings/lines?${params.toString()}`);
  }

  async saveMonitoredLines(config) {
    return this.request('/api/settings/lines', {
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  // Production Tonnage
  async addProductionEntry(data) {
    return this.request('/api/production/entry', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Préférence d'affichage du chart Énergie sur Mesures Amont/Aval (super_admin only)
  async updateEnergyChartSide(siteId, side) {
    return this.request(`/api/settings/sites/${encodeURIComponent(siteId)}/energy-chart-side`, {
      method: 'PUT',
      body: JSON.stringify({ side }),
    });
  }

  // Heure de bascule du jour energetique (0-23) — super_admin only
  async getSiteDayStartHour(siteId) {
    return this.request(`/api/settings/sites/${encodeURIComponent(siteId)}/day-start-hour`);
  }
  async updateSiteDayStartHour(siteId, hour) {
    return this.request(`/api/settings/sites/${encodeURIComponent(siteId)}/day-start-hour`, {
      method: 'PUT',
      body: JSON.stringify({ day_start_hour: hour }),
    });
  }

  // Seuils d'alerte (PF / THD / tension) — super_admin only.
  // Defauts cote backend : PF<0.85, THD>10%, V hors [90%, 110%].
  async getSiteAlertThresholds(siteId) {
    return this.request(`/api/settings/sites/${encodeURIComponent(siteId)}/alert-thresholds`);
  }
  async updateSiteAlertThresholds(siteId, thresholds) {
    return this.request(`/api/settings/sites/${encodeURIComponent(siteId)}/alert-thresholds`, {
      method: 'PUT',
      body: JSON.stringify(thresholds),
    });
  }

  // Whitelist de dashboards visibles pour un user (super_admin / admin_client)
  // enabled_dashboards = liste de cles (peut etre []) OU null pour repasser au defaut.
  async updateUserEnabledDashboards(userId, enabled_dashboards) {
    return this.request(`/api/admin/users/${encodeURIComponent(userId)}/enabled-dashboards`, {
      method: 'PUT',
      body: JSON.stringify({ enabled_dashboards }),
    });
  }

  // Idem mais cote admin_client (pour ses propres utilisateurs uniquement)
  async updateClientAdminUserEnabledDashboards(userId, enabled_dashboards) {
    return this.request(`/api/client-admin/users/${encodeURIComponent(userId)}/enabled-dashboards`, {
      method: 'PUT',
      body: JSON.stringify({ enabled_dashboards }),
    });
  }

  // Reinitialiser le mot de passe d'un utilisateur (super_admin).
  async resetUserPassword(userId, new_password) {
    return this.request(`/api/admin/users/${encodeURIComponent(userId)}/reset-password`, {
      method: 'PUT',
      body: JSON.stringify({ new_password }),
    });
  }

  // Idem cote admin_client (uniquement ses propres utilisateurs).
  async resetClientAdminUserPassword(userId, new_password) {
    return this.request(`/api/client-admin/users/${encodeURIComponent(userId)}/reset-password`, {
      method: 'PUT',
      body: JSON.stringify({ new_password }),
    });
  }

  // Audit log d'un client (super_admin only). action_prefix optionnel pour filtrer.
  async getClientAuditLogs(clientId, { limit = 50, action_prefix = null } = {}) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (action_prefix) params.set('action_prefix', action_prefix);
    return this.request(`/api/admin/clients/${encodeURIComponent(clientId)}/audit-logs?${params.toString()}`);
  }

  // Visibilite des graphes du dashboard Mesures Amont/Aval — super_admin only
  async updateEnabledCharts(siteId, enabled_charts) {
    return this.request(`/api/settings/sites/${encodeURIComponent(siteId)}/enabled-charts`, {
      method: 'PUT',
      body: JSON.stringify({ enabled_charts }),
    });
  }

  // Affichage des donnees de temperature sur les dashboards — super_admin only
  async updateTemperatureEnabled(siteId, enabled) {
    return this.request(`/api/settings/sites/${encodeURIComponent(siteId)}/temperature-enabled`, {
      method: 'PUT',
      body: JSON.stringify({ temperature_enabled: !!enabled }),
    });
  }

  // Affichage du THD sur les dashboards — super_admin only
  // Duree d'affichage des alertes sur les tableaux de bord, en jours.
  async updateAlertWindow(siteId, jours) {
    return this.request(`/api/settings/sites/${encodeURIComponent(siteId)}/alert-window`, {
      method: 'PUT',
      body: JSON.stringify({ alert_window_days: jours }),
    });
  }

  // Facteur d'emission CO2 du site, en kg par kWh consomme.
  async updateCarbonFactor(siteId, facteur) {
    return this.request(`/api/settings/sites/${encodeURIComponent(siteId)}/carbon-factor`, {
      method: 'PUT',
      body: JSON.stringify({ carbon_factor_kg_per_kwh: facteur }),
    });
  }

  // Seuil de puissance sous lequel une ligne est consideree a l'arret.
  async updateIdleThreshold(siteId, thresholdKw) {
    return this.request(`/api/settings/sites/${encodeURIComponent(siteId)}/idle-threshold`, {
      method: 'PUT',
      body: JSON.stringify({ idle_threshold_kw: thresholdKw }),
    });
  }

  async updateThdEnabled(siteId, enabled) {
    return this.request(`/api/settings/sites/${encodeURIComponent(siteId)}/thd-enabled`, {
      method: 'PUT',
      body: JSON.stringify({ thd_enabled: !!enabled }),
    });
  }

  // Affichage KPI "Marge de capacité disjoncteur" (15min vs calibre In) — super_admin only
  async updateCableLoadEnabled(siteId, enabled) {
    return this.request(`/api/settings/sites/${encodeURIComponent(siteId)}/cable-load-enabled`, {
      method: 'PUT',
      body: JSON.stringify({ cable_load_enabled: !!enabled }),
    });
  }

  async updateDemand15minEnabled(siteId, enabled) {
    return this.request(`/api/settings/sites/${encodeURIComponent(siteId)}/demand-15min-enabled`, {
      method: 'PUT',
      body: JSON.stringify({ demand_15min_enabled: !!enabled }),
    });
  }

  // Tonnage — validation des saisies par le chef de production.
  // L'operateur saisit sans demarche prealable : la saisie arrive directement
  // dans la liste d'attente ci-dessous.
  async getPendingValidations(siteId) {
    return this.request(`/api/production/entries/pending-validation?site_id=${encodeURIComponent(siteId)}`);
  }
  async validateTonnageEntry(entryId, tonnageValidated, chefComment = null) {
    return this.request(`/api/production/entries/${entryId}/validate`, {
      method: 'PUT',
      body: JSON.stringify({ tonnage_validated: tonnageValidated, chef_comment: chefComment }),
    });
  }
  async rejectTonnageEntry(entryId, reason) {
    return this.request(`/api/production/entries/${entryId}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  }
}

// Instance unique exportée
const api = new ApiService();
export default api;
